import { useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { TextField } from './ui/Field'
import { api, extractApiError } from '../lib/api'
import { CONTAINS_LETTER } from '../lib/validators'

const folderSchema = z.object({
  name: z
    .string({ message: 'Project Name is required.' })
    .trim()
    .min(1, 'Project Name is required.')
    .min(2, 'Project Name must be at least 2 characters.')
    .max(100, 'Project Name must be at most 100 characters.')
    // Numbers-only / symbols-only names aren't meaningful — require a letter.
    .regex(CONTAINS_LETTER, 'Project Name must contain at least one letter.'),
})

export function FolderCreateDialog({
  open,
  defaultSpaceId,
  onClose,
  onCreated,
}) {
  const qc = useQueryClient()
  const spaceId = defaultSpaceId

  const form = useForm({
    resolver: zodResolver(folderSchema),
    defaultValues: { name: '' },
  })

  useEffect(() => {
    if (open) form.reset({ name: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultSpaceId])

  const createMutation = useMutation({
    mutationFn: async (values) => {
      if (!spaceId) throw new Error('No Space context.')
      const res = await api.post(`/spaces/${spaceId}/folders`, {
        name: values.name.trim(),
      })
      return res.data
    },
    onSuccess: (created) => {
      toast.success('Project has been created successfully.')
      void qc.invalidateQueries({ queryKey: ['folders', spaceId] })
      // The backend also creates a starter List inside the new Project — refresh
      // the Lists tree so it appears immediately (not only via realtime).
      void qc.invalidateQueries({ queryKey: ['lists', spaceId] })
      onClose()
      if (spaceId) onCreated?.(created.id, spaceId)
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.name) form.setError('name', { message: fields.name })
      else toast.error(message)
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Project"
      description="Projects sit inside a Space and group Lists and Documents."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="folder-create-form"
            loading={createMutation.isPending}
            disabled={!spaceId}
          >
            Create
          </Button>
        </>
      }
    >
      <form
        id="folder-create-form"
        onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
        noValidate
        className="space-y-4"
      >
        <TextField
          label="Project Name"
          required
          placeholder="Enter a Project Name"
          minLength={2}
          maxLength={80}
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />
      </form>
    </Modal>
  )
}
